import { useEffect, useLayoutEffect, useState } from "react";
import { usePathname } from "next/navigation";

const STORAGE_KEY = "resources:lastListingPath";

// Listing pages have at most one segment after "resources" (category slug, or
// "en" for the main page) — two if it's a category page's "/en" variant.
// Article pages always have one segment more than that. Used instead of
// document.referrer, which never updates across Next.js client-side
// navigations and would keep pointing at whatever page originally loaded
// the tab.
function isListingPath(pathname: string): boolean {
  const segments = pathname.split("/").filter(Boolean);
  if (segments[0] !== "resources") return false;
  if (segments.length <= 2) return true;
  return segments.length === 3 && segments[2] === "en";
}

export function recordIfListingPath(pathname: string) {
  if (typeof window === "undefined") return;
  if (isListingPath(pathname)) {
    window.sessionStorage.setItem(STORAGE_KEY, pathname);
  }
}

export function getLastListingPath(): string | null {
  if (typeof window === "undefined") return null;
  return window.sessionStorage.getItem(STORAGE_KEY);
}

// ---------------------------------------------------------------------------
// Returning to a listing where the visitor left it.
//
// Coming back from an article (browser back, or the article's back arrow, which
// goes back in history when the listing is the previous page) restores the
// listing's own state — batches revealed with « show more », active topic filter —
// and then its scroll position. Without the state, the page would be too short to
// scroll back down to the card that was clicked. A listing reached any other way
// (navbar, fresh link) starts from the top, as before.

const LISTING_PREFIX = "resources:listing:";

let currentPath: string | null = null;
let previousPath: string | null = null;
// Path a history traversal (back/forward) landed on, until that listing restores itself.
let traversedTo: string | null = null;

export function recordPathChange(pathname: string) {
  if (pathname === currentPath) return;
  previousPath = currentPath;
  currentPath = pathname;
}

/** Path of the page shown before the current one in this tab (client-side navigations only). */
export function getPreviousPath(): string | null {
  return previousPath;
}

/** Called on `popstate`; `location` already points at the destination by then. */
export function recordHistoryTraversal() {
  traversedTo = window.location.pathname;
}

function readListing<T>(key: string): T | undefined {
  try {
    const raw = window.sessionStorage.getItem(LISTING_PREFIX + key);
    return raw === null ? undefined : (JSON.parse(raw) as T);
  } catch {
    return undefined;
  }
}

function writeListing(key: string, value: unknown) {
  try {
    window.sessionStorage.setItem(LISTING_PREFIX + key, JSON.stringify(value));
  } catch {
    // Storage full or blocked: the listing simply won't be restored.
  }
}

/**
 * `useState` for a listing's UI state, saved per page and given back when the visitor
 * returns to that page through history.
 */
export function useListingState<T>(name: string, initial: T): [T, (value: T | ((prev: T) => T)) => void] {
  const pathname = usePathname();
  const key = `${pathname}:${name}`;
  const [value, setValue] = useState<T>(() =>
    typeof window !== "undefined" && traversedTo === pathname ? (readListing<T>(key) ?? initial) : initial,
  );
  useEffect(() => writeListing(key, value), [key, value]);
  return [value, setValue];
}

/** Saves the listing's scroll position and puts it back when the visitor returns through history. */
export function useListingScroll() {
  const pathname = usePathname();
  useLayoutEffect(() => {
    const key = `${pathname}:scrollY`;
    if (traversedTo === pathname) {
      traversedTo = null;
      const y = readListing<number>(key);
      // "instant": the html element scrolls smoothly, which would show the whole ride down.
      if (typeof y === "number") window.scrollTo({ top: y, behavior: "instant" });
    } else {
      writeListing(key, 0);
    }

    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => writeListing(key, window.scrollY));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
    };
  }, [pathname]);
}
